/**
 * Danger tiers. The single most important concept in this product.
 *
 *  1 — Safe.     Reads, filters, exports. No confirmation. Neutral styling.
 *  2 — Write.    Reversible change. One confirm step, amber accent, change summary.
 *  3 — Override. Bypasses app rules or is irreversible. Requires a consequence
 *                panel, a mandatory free-text reason, and type-to-confirm.
 *
 * The tier drives colour, icon and confirmation choreography identically
 * everywhere, so an operator learns it once. Never place a Tier 3 control
 * adjacent to a Tier 1 control.
 */
export const DANGER_TIERS = [1, 2, 3] as const;
export type DangerTier = (typeof DANGER_TIERS)[number];

/**
 * Granular capabilities. Named `<domain>.<verb>` so the permission matrix in
 * Settings can be rendered by grouping on the domain segment.
 */
export type AdminCapability =
  | 'dashboard.view'
  // Revenue and growth figures. Deliberately NOT bundled into `dashboard.view`, which
  // every role holds: the ops home is "what needs me right now" and is safe for a
  // moderator, whereas this is company financials and belongs to engineer + owner.
  | 'metrics.view'
  | 'leagues.view'
  | 'leagues.edit'
  | 'leagues.override'
  | 'leagues.delete'
  | 'users.view'
  | 'users.edit'
  | 'users.tokens'
  | 'users.impersonate'
  | 'users.delete'
  | 'squads.view'
  | 'squads.edit'
  | 'squads.override'
  | 'communities.view'
  | 'communities.moderate'
  | 'communities.ban'
  | 'content.view'
  | 'content.edit'
  | 'content.publish'
  | 'content.delete'
  | 'dataops.view'
  | 'dataops.run'
  | 'dataops.destructive'
  | 'notifications.view'
  | 'notifications.send'
  | 'philanthropy.view'
  | 'philanthropy.manage'
  | 'audit.view'
  | 'audit.export'
  | 'settings.view'
  | 'settings.manage';

export type AdminRole = 'engineer' | 'content' | 'moderator' | 'owner';

export const ROLE_LABELS: Record<AdminRole, string> = {
  owner: 'Owner',
  engineer: 'Engineer',
  content: 'Content',
  moderator: 'Moderator',
};

const ENGINEER: AdminCapability[] = [
  'dashboard.view',
  'metrics.view',
  'leagues.view',
  'leagues.edit',
  'leagues.override',
  'users.view',
  'users.edit',
  'users.impersonate',
  'squads.view',
  'squads.edit',
  'squads.override',
  'communities.view',
  'content.view',
  'dataops.view',
  'dataops.run',
  'dataops.destructive',
  'notifications.view',
  'philanthropy.view',
  'audit.view',
  'audit.export',
  'settings.view',
];

const CONTENT: AdminCapability[] = [
  'dashboard.view',
  'leagues.view',
  'users.view',
  'squads.view',
  'communities.view',
  'content.view',
  'content.edit',
  'content.publish',
  'content.delete',
  'notifications.view',
  'notifications.send',
  'audit.view',
  'settings.view',
];

const MODERATOR: AdminCapability[] = [
  'dashboard.view',
  'leagues.view',
  'users.view',
  'users.edit',
  'squads.view',
  'communities.view',
  'communities.moderate',
  'communities.ban',
  'content.view',
  'audit.view',
  'settings.view',
];

/**
 * Role → capability matrix.
 *
 * Note what content and moderator deliberately lack: `leagues.override`,
 * `dataops.destructive`, `users.tokens`, `users.delete`, `philanthropy.manage`
 * and `leagues.delete`. Those are the season-corrupting and money-adjacent
 * operations, and the brief requires those roles cannot reach them.
 *
 * This is a UI affordance only. mesh-admin-api enforces the same matrix
 * server-side; hiding a button is not access control.
 */
export const ROLE_CAPABILITIES: Record<AdminRole, readonly AdminCapability[]> = {
  owner: [
    ...new Set<AdminCapability>([
      ...ENGINEER,
      ...CONTENT,
      ...MODERATOR,
      'leagues.delete',
      'users.tokens',
      'users.delete',
      'philanthropy.manage',
      'settings.manage',
    ]),
  ],
  engineer: ENGINEER,
  content: CONTENT,
  moderator: MODERATOR,
};

export function roleHasCapability(role: AdminRole, capability: AdminCapability): boolean {
  return ROLE_CAPABILITIES[role].includes(capability);
}
